import { getBackendUrl } from '../config/urls';

function parseJsonField(value) {
  if (value == null) return null;
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    try {
      return JSON.parse(trimmed);
    } catch {
      return null;
    }
  }
  return null;
}

function firstDefined(...values) {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    return value;
  }
  return null;
}

function parseDescriptionMeta(description) {
  if (!description || typeof description !== 'string') {
    return { serviceType: null, commentaires: null, colis: null };
  }
  const serviceMatch = description.match(/\[service:\s*([^\]]+)\]/i);
  const commentMatch = description.match(/Commentaires:\s*(.+?)(?:\n|$)/i);
  const colisMatch = description.match(/Colis:\s*(.+?)(?:\n|$)/i);
  let colis = null;
  if (colisMatch) {
    try {
      colis = JSON.parse(colisMatch[1].trim());
    } catch {
      colis = colisMatch[1].trim();
    }
  }
  return {
    serviceType: serviceMatch ? serviceMatch[1].trim() : null,
    commentaires: commentMatch ? commentMatch[1].trim() : null,
    colis,
  };
}

const HOUSING_LABELS = {
  maison: 'Maison',
  appartement: 'Appartement',
  bureau: 'Bureau',
  studio: 'Studio',
  local: 'Local',
};

const MISSION_TYPE_LABELS = {
  demenagement: 'Déménagement',
  transport: 'Transport',
  transport_international: 'Transport international',
  along_way: 'En chemin',
};

const WORKER_OPTION_LABELS = {
  camion_seul: 'Camion seul',
  avec_travailleurs: 'Camion avec travailleurs',
};

const TIME_SLOT_LABELS = {
  morning: 'Matin',
  afternoon: 'Après-midi',
};

const ADDITIONAL_SERVICE_LABELS = {
  montage_demontage: 'Montage/Démontage',
  emballage: 'Emballage professionnel',
  garde_meuble: 'Garde-meuble',
  carton: 'Fourniture de cartons',
  cartons: 'Fourniture de cartons',
  montage: 'Montage',
  demontage: 'Démontage',
  nettoyage: 'Nettoyage',
};

const PICKUP_TYPE_LABELS = {
  home: 'Retrait à domicile',
  domicile: 'Retrait à domicile',
  depot_transporteur: 'Dépôt chez le transporteur',
};

const CANCELLATION_REASON_LABELS = {
  negotiation_refused: 'Négociation refusée',
  client_no_response: 'Le client n’a pas répondu à temps',
  mover_no_response: 'Le transporteur n’a pas répondu à temps',
  no_longer_needed: 'Le client n’a plus besoin du service',
  change_date: 'Changement de date refusé',
  change_time: 'Changement d’heure refusé',
  change_location: 'Changement d’adresse refusé',
};

const SIZE_KEYS = ['grande', 'moyen', 'petit'];
const SIZE_LABELS = { grande: 'Grand', moyen: 'Moyen', petit: 'Petit' };

function labelize(map, value) {
  if (value == null || value === '') return null;
  const key = String(value).toLowerCase();
  return map[key] || String(value);
}

function parseBool(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    if (['true', '1', 'oui', 'yes'].includes(v)) return true;
    if (['false', '0', 'non', 'no'].includes(v)) return false;
  }
  return null;
}

function toCount(value) {
  const n = parseInt(value, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function resolveMediaUrl(url) {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  if (/^(https?:|data:|blob:)/i.test(trimmed)) return trimmed;
  if (/^(file:|content:|ph:)/i.test(trimmed)) return null;
  const base = getBackendUrl().replace(/\/$/, '');
  return `${base}${trimmed.startsWith('/') ? '' : '/'}${trimmed}`;
}

function photoList(value) {
  if (!value) return [];
  const list = Array.isArray(value) ? value : [value];
  return list
    .map((item) => resolveMediaUrl(typeof item === 'string' ? item : item?.url || item?.uri))
    .filter(Boolean);
}

/** Articles + photos par taille ; null si rien n'a été saisi. */
function buildSizedBlock(name, counts, photos) {
  const sizes = SIZE_KEYS.map((size) => ({
    key: size,
    label: SIZE_LABELS[size],
    count: toCount(counts?.[size]),
    photos: photoList(photos?.[size]),
  })).filter((s) => s.count > 0 || s.photos.length > 0);
  if (sizes.length === 0) return null;
  return { name, sizes };
}

function formatDateTime(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatAmount(value) {
  const n = parseFloat(value);
  return Number.isFinite(n) ? `${n.toFixed(2).replace('.', ',')} DT` : null;
}

function resolveMissionType(services, nested, descMeta) {
  const candidates = [services.serviceType, services.service_type, nested.serviceType, descMeta.serviceType];
  for (const c of candidates) {
    const key = c != null ? String(c).toLowerCase() : '';
    if (MISSION_TYPE_LABELS[key]) return key;
  }
  if (services.alongWay) return 'along_way';
  if (services.country) return 'transport_international';
  return null;
}

function resolveAdditionalServices(services, nested) {
  const raw = firstDefined(
    services.additional_services,
    services.additionalServices,
    nested.additionalServices
  );
  const ids = [];
  if (Array.isArray(raw)) ids.push(...raw);
  else if (raw) ids.push(raw);
  const web = services.webSupplementalServices;
  if (web && typeof web === 'object' && !Array.isArray(web)) {
    Object.entries(web).forEach(([key, enabled]) => {
      if (enabled === true) ids.push(key);
    });
  }
  const labels = ids
    .map((item) => labelize(ADDITIONAL_SERVICE_LABELS, item))
    .filter(Boolean);
  return [...new Set(labels)];
}

function resolveWorkerOption({ services, missionType, isQuickService, additionalServices, propertyType }) {
  const explicit = [services.workerOption, services.moverServiceMode, services.serviceType]
    .map((v) => (v != null ? String(v).toLowerCase() : ''))
    .find((v) => WORKER_OPTION_LABELS[v]);
  if (explicit) return explicit;
  if (isQuickService || (missionType !== 'demenagement' && missionType !== 'transport')) return null;
  if (additionalServices.length > 0 || propertyType === 'appartement') return 'avec_travailleurs';
  return 'camion_seul';
}

function buildHistory(services) {
  const items = [];
  const push = (label, value) => {
    if (value != null && String(value).trim() !== '') items.push({ label, value: String(value) });
  };

  push('Prix proposé par le client', formatAmount(services.clientProposedPrice));
  push('Proposition du client le', formatDateTime(services.clientPriceProposedAt));
  push('Proposition du transporteur le', formatDateTime(services.moverPriceProposedAt));

  if (services.negotiationRefusedAt || services.negotiationRefusedBy) {
    const by = services.negotiationRefusedBy === 'client'
      ? 'le client'
      : services.negotiationRefusedBy === 'mover'
        ? 'le transporteur'
        : null;
    push('Négociation refusée par', by);
    push('Prix refusé', formatAmount(services.negotiationRefusedPrice));
    push('Refus le', formatDateTime(services.negotiationRefusedAt));
  }

  if (services.clientResponseTimedOut) {
    push('Délai de réponse du client dépassé', formatDateTime(services.clientResponseTimedOutAt) || 'Oui');
  }
  if (services.moverResponseTimedOut) {
    push('Délai de réponse du transporteur dépassé', formatDateTime(services.moverResponseTimedOutAt) || 'Oui');
  }
  push('Motif de refus du transporteur', services.moverRefusalReason);

  if (services.clientCancellationReason) {
    push('Motif d’annulation', labelize(CANCELLATION_REASON_LABELS, services.clientCancellationReason));
  }
  push('Annulée par le client le', formatDateTime(services.clientCancelledAt));
  push('Annulée par l’admin le', formatDateTime(services.adminCancelledAt));
  push('Statut modifié par l’admin le', formatDateTime(services.adminStatusChangedAt));
  push('Motif admin', services.adminStatusReason);

  return items;
}

export function parseMissionFormData(quote) {
  if (!quote) {
    return { fields: [], hasData: false, rooms: [], articlePhotos: [], history: [] };
  }

  const services = parseJsonField(quote.services) || {};
  const nested = services.serviceDetails && typeof services.serviceDetails === 'object'
    ? services.serviceDetails
    : {};
  const descMeta = parseDescriptionMeta(quote.description);

  const missionType = resolveMissionType(services, nested, descMeta);
  const isQuickService = services.isQuickService === true;
  const additionalServices = resolveAdditionalServices(services, nested);

  const propertyTypeRaw = firstDefined(
    services.propertyType,
    nested.propertyType,
    quote.housing_type,
    services.logement
  );
  const propertyType = propertyTypeRaw != null ? String(propertyTypeRaw).toLowerCase() : null;

  const workerOption = resolveWorkerOption({
    services,
    missionType,
    isQuickService,
    additionalServices,
    propertyType,
  });
  const withWorkers = workerOption === 'avec_travailleurs';
  const showHousing = withWorkers || (!workerOption && Boolean(quote.housing_type));
  const isApartment = propertyType === 'appartement';

  const floorRaw = firstDefined(services.floor, nested.floor, quote.floor, services.etage);
  const elevatorRaw = firstDefined(services.hasElevator, nested.hasElevator, quote.has_elevator, services.ascenseur);
  const elevator = parseBool(elevatorRaw);
  const timeSlotRaw = firstDefined(services.time_slot, services.timeSlot, nested.timeSlot);

  const isDemenagement = missionType === 'demenagement';

  const fields = [
    {
      key: 'serviceType',
      label: 'Type de mission',
      value: missionType ? MISSION_TYPE_LABELS[missionType] : null,
    },
    {
      key: 'isQuickService',
      label: 'Service rapide',
      value: isQuickService ? 'Oui' : null,
    },
    {
      key: 'workerOption',
      label: 'Formule',
      value: workerOption ? WORKER_OPTION_LABELS[workerOption] : null,
    },
    {
      key: 'propertyType',
      label: 'Type de propriété',
      value: showHousing ? labelize(HOUSING_LABELS, propertyType) : null,
    },
    {
      key: 'unitNumber',
      label: 'N° appartement / bureau',
      value: showHousing ? firstDefined(services.unit_number, services.unitNumber, nested.unit_number) : null,
    },
    {
      key: 'floor',
      label: 'Étage',
      value: showHousing && isApartment && floorRaw != null
        ? (floorRaw === 0 || floorRaw === '0' ? 'Rez-de-chaussée' : String(floorRaw))
        : null,
    },
    {
      key: 'hasElevator',
      label: 'Ascenseur',
      value: showHousing && isApartment && elevator != null ? (elevator ? 'Oui' : 'Non') : null,
    },
    {
      key: 'additionalServices',
      label: 'Services supplémentaires',
      value: withWorkers || !workerOption
        ? (additionalServices.length ? additionalServices.join(', ') : (withWorkers ? 'Aucun' : null))
        : null,
    },
    {
      key: 'volumeM3',
      label: 'Volume estimé (m³)',
      value: firstDefined(quote.volume_m3, services.volume_m3, services.volume),
    },
    {
      key: 'truckType',
      label: 'Type de véhicule',
      value: firstDefined(services.vehicleType, quote.truck_type, services.truck_type),
    },
    { key: 'timeSlot', label: 'Créneau horaire', value: labelize(TIME_SLOT_LABELS, timeSlotRaw) },
    {
      key: 'article',
      label: 'Article / objet à transporter',
      value: isDemenagement ? null : firstDefined(services.article, nested.article),
    },
    { key: 'country', label: 'Pays', value: firstDefined(services.country) },
    { key: 'articleType', label: 'Type d’article', value: firstDefined(services.articleType) },
    {
      key: 'baggageWeightKg',
      label: 'Poids bagages (kg)',
      value: firstDefined(services.baggageWeightKg, services.baggage_weight_kg),
    },
    {
      key: 'pickupType',
      label: 'Type de retrait',
      value: labelize(PICKUP_TYPE_LABELS, firstDefined(services.pickupType, services.pickup_type)),
    },
    {
      key: 'packages',
      label: 'Colis / packages',
      value: (() => {
        const colis = firstDefined(services.packages, services.colis, descMeta.colis);
        if (colis == null) return null;
        return typeof colis === 'object' ? JSON.stringify(colis) : String(colis);
      })(),
    },
    { key: 'manutention', label: 'Manutention', value: firstDefined(services.manutention) },
    {
      key: 'notes',
      label: 'Notes du client',
      value: firstDefined(nested.notes, services.notes, descMeta.commentaires),
    },
    { key: 'description', label: 'Message / description', value: quote.description || null },
  ].map((field) => ({
    ...field,
    display: field.value != null && String(field.value).trim() !== '' ? String(field.value) : null,
  }));

  const rooms = [];
  if (isDemenagement || !missionType) {
    const cuisine = buildSizedBlock(
      'Cuisine',
      firstDefined(nested.cuisine, services.cuisine),
      firstDefined(nested.cuisinePhotos, services.cuisinePhotos)
    );
    if (cuisine) rooms.push(cuisine);
    const chambres = firstDefined(nested.chambres, services.chambres);
    if (Array.isArray(chambres)) {
      chambres.forEach((chambre, index) => {
        const name = chambre?.nom && String(chambre.nom).trim()
          ? String(chambre.nom).trim()
          : `Chambre ${index + 1}`;
        const block = buildSizedBlock(name, chambre?.articles, chambre?.photos);
        if (block) rooms.push(block);
      });
    }
  }

  const articlePhotos = [
    ...photoList(services.articlePhotos),
    ...photoList(services.valisePhoto),
    ...photoList(services.valiseExteriorPhoto),
  ];

  const history = buildHistory(services);

  return {
    fields,
    hasData: fields.some((f) => f.display) || rooms.length > 0 || articlePhotos.length > 0,
    missionType,
    workerOption,
    rooms,
    articlePhotos,
    history,
  };
}
